import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AnnouncementPreview } from "@/components/AnnouncementBar";

const inputCls = "w-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#6D28D9]/60";
const THEMES = ["purple", "gold", "emerald", "rose"];
const PRESETS = [
  { title: "Happy Janmashtami!", message: "May Lord Krishna bless your portfolio with prosperity.", tag: "Start investing with a minimum of ₹10,000 · buy in 3 simple steps", theme: "purple" },
  { title: "Happy Diwali!", message: "Light up your portfolio this festive season.", tag: "Festive allocations open · limited windows", theme: "gold" },
  { title: "Happy Independence Day!", message: "Invest in the India growth story.", tag: "New pre-IPO names added this week", theme: "emerald" },
  { title: "Happy New Year!", message: "Start the year with a pre-IPO plan.", tag: "Book a free 15-min consultation", theme: "rose" },
];

const Field = ({ label, children }) => (
  <label className="block"><span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</span><div className="mt-1.5">{children}</div></label>
);

export const BannerTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/announcement").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { enabled, title, message, tag, cta_label, cta_target, theme } = form;
      const res = await api.put("/admin/settings/announcement", { enabled, title, message, tag, cta_label, cta_target, theme });
      setForm(res.data);
      toast.success("Banner updated — visitors will see it on their next visit.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!form) return <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading banner…</p>;

  return (
    <div data-testid="admin-banner-tab">
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight">Top Banner</h2>
        <p className="mt-1 text-sm text-slate-500">Change the festival greeting or offer shown under the navbar on the home page — no developer needed.</p>
      </div>

      <div className="mt-6">
        <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Live preview</p>
        <div className="mt-2 border border-slate-200"><AnnouncementPreview data={form} /></div>
      </div>

      <form data-testid="admin-banner-form" onSubmit={save} className="mt-6 grid gap-5 border border-slate-200 bg-[#F6F4FD]/70 p-6 md:grid-cols-2">
        <div className="md:col-span-2 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.title} type="button" data-testid={`admin-banner-preset-${p.theme}`} onClick={() => setForm((f) => ({ ...f, ...p, enabled: true }))} className="border border-slate-200 bg-white px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-600 transition-colors hover:border-[#6D28D9]/50 hover:text-[#6D28D9]">{p.title}</button>
          ))}
        </div>
        <Field label="Headline"><input data-testid="admin-banner-title" required maxLength={60} value={form.title} onChange={set("title")} className={inputCls} /></Field>
        <Field label="Message"><input data-testid="admin-banner-message" maxLength={160} value={form.message || ""} onChange={set("message")} className={inputCls} /></Field>
        <Field label="Small tag line (offer / update)"><input data-testid="admin-banner-tag" maxLength={80} value={form.tag || ""} onChange={set("tag")} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Button text"><input data-testid="admin-banner-cta-label" maxLength={24} value={form.cta_label || ""} onChange={set("cta_label")} placeholder="Explore" className={inputCls} /></Field>
          <Field label="Button link (#section or /page)"><input data-testid="admin-banner-cta-target" maxLength={120} value={form.cta_target || ""} onChange={set("cta_target")} placeholder="#opportunities" className={inputCls} /></Field>
        </div>
        <Field label="Colour">
          <div className="flex gap-2">
            {THEMES.map((t) => (
              <button key={t} type="button" data-testid={`admin-banner-theme-${t}`} onClick={() => setForm((f) => ({ ...f, theme: t }))} className={`px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] ${form.theme === t ? "bg-[#6D28D9] text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{t}</button>
            ))}
          </div>
        </Field>
        <label className="flex items-center gap-3 self-end">
          <input data-testid="admin-banner-enabled" type="checkbox" checked={!!form.enabled} onChange={set("enabled")} className="h-4 w-4 accent-[#6D28D9]" />
          <span className="text-sm text-slate-700">Show banner on the site</span>
        </label>
        <div className="md:col-span-2 flex items-center justify-between">
          <p className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-400">{form.updated_at ? `Last changed ${new Date(form.updated_at).toLocaleString("en-IN")}` : "Using default banner"}</p>
          <button data-testid="admin-banner-save" type="submit" disabled={saving} className="flex items-center gap-2 bg-[#6D28D9] px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Publish banner</>}
          </button>
        </div>
      </form>
      <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Megaphone className="h-3.5 w-3.5 text-[#6D28D9]" /> The banner also appears as the first slide of the home carousel.</p>
    </div>
  );
};
